import isEqual from 'lodash.isequal';
import React, { useEffect, useMemo } from 'react';
import { NumericFormat } from 'react-number-format';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { CHART_DATA_KEYS, CHART_TYPES } from '_constants/charts';
import { FIELD_KEYS } from '_constants/forms';
import { ICON_ALIASES } from '_constants/icons';
import { REPORT_KEYS } from '_constants/reports';
import { theme } from '_constants/theme';
import { createChartData, createChartProps } from '_helpers/charts';
import { hexToRGBA } from '_helpers/theme';
import { usePrevious } from '_hooks';
import { selectActiveMainProductId } from '_store/order/selector';
import { selectData, selectFilters, selectParams } from '_store/reports/selector';
import { getExpensesGraph, getIncomeGraph } from '_store/reports/slice';
import { selectCustomerId } from '_store/user/selector';

import Chart from 'components/atomic/Chart';
import Icon from 'components/atomic/Icon';
import Card from 'components/atomic/layouts/Card';
import Text from 'components/atomic/layouts/Text';

import Filters from './Filters';

const StyledChartContainer = styled.div`
  height: 300px;

  @media ${theme.devices.tablet} {
  }
`;

const StyledSubtitleContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.smallX}px;
  margin-bottom: ${theme.spaces.medium}px;
`;

const StyledRateUpContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${theme.spaces.small2X}px;
  padding-top: ${theme.spaces.small2X}px;
  padding-bottom: ${theme.spaces.small2X}px;
  padding-right: ${theme.spaces.smallX}px;
  padding-left: ${theme.spaces.smallX}px;
  border: 1px solid ${hexToRGBA(theme.colors.green.primary, 0.08)};
  border-radius: 16px;
  background: ${hexToRGBA(theme.colors.green.primary, 0.08)};
`;

const StyledRateDownContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${theme.spaces.small2X}px;
  padding-top: ${theme.spaces.small2X}px;
  padding-bottom: ${theme.spaces.small2X}px;
  padding-right: ${theme.spaces.smallX}px;
  padding-left: ${theme.spaces.smallX}px;
  border: 1px solid ${hexToRGBA(theme.colors.red.primary, 0.08)};
  border-radius: 16px;
  background: ${hexToRGBA(theme.colors.red.primary, 0.08)};
`;

const BusinessSnapshotView = () => {
  const dispatch = useDispatch();
  const customerId = useSelector(selectCustomerId);
  const productId = useSelector(selectActiveMainProductId);
  const prevProductId = usePrevious(productId);
  const expensesReportParams = useSelector(selectParams(REPORT_KEYS.expensesGraph));
  const prevExpensesReportParams = usePrevious(expensesReportParams);
  const expensesData = useSelector(selectData(REPORT_KEYS.expensesGraph));
  const expensesFilters = useSelector(selectFilters(REPORT_KEYS.expensesGraph));
  const incomeData = useSelector(selectData(REPORT_KEYS.incomeGraph));

  useEffect(() => {
    if (customerId && productId && !isEqual(prevExpensesReportParams, expensesReportParams)) {
      dispatch(getExpensesGraph({ customerId, productId }));
      dispatch(getIncomeGraph({ customerId, productId }));
    }
  }, [customerId, productId, prevExpensesReportParams, expensesReportParams]);

  useEffect(() => {
    if (customerId && productId && expensesReportParams && prevProductId !== productId) {
      dispatch(getExpensesGraph({ customerId, productId }));
      dispatch(getIncomeGraph({ customerId, productId }));
    }
  }, [customerId, prevProductId, productId, expensesReportParams]);

  const chartProps = useMemo(
    () =>
      createChartProps({
        type: CHART_TYPES.line,
        [FIELD_KEYS.filterType]: expensesFilters[FIELD_KEYS.filterType],
        data: createChartData({
          [FIELD_KEYS.type]: CHART_TYPES.line,
          [FIELD_KEYS.filterType]: expensesFilters[FIELD_KEYS.filterType],
          data: {
            [CHART_DATA_KEYS.income]: incomeData?.expensesGraph || {},
            [CHART_DATA_KEYS.expenses]: expensesData?.expensesGraph || {},
          },
        }),
      }),
    [expensesData?.expensesGraph, incomeData?.expensesGraph]
  );

  return (
    <Card fullWidth>
      <Text size={theme.fontSizes.medium} weight={600}>
        Business Snapshot
      </Text>
      <StyledSubtitleContainer>
        <Text size={theme.fontSizes.small} weight={600} color={theme.colors.gray.secondary}>
          Total Expenses:
        </Text>
        <Text size={theme.fontSizes.small} weight={600} ignoreSpan>
          <NumericFormat
            displayType="text"
            value={expensesData?.totalExpenses}
            prefix="$"
            thousandSeparator=","
            decimalScale={0}
            fixedDecimalScale
          />
        </Text>
        {expensesData?.totalExpensesChangePercentage >= 0 && (
          <StyledRateUpContainer>
            <Text size={theme.fontSizes.smallX} weight={700} color={theme.colors.green.primary} inline ignoreSpan>
              <NumericFormat
                displayType="text"
                value={expensesData?.totalExpensesChangePercentage}
                suffix="%"
                decimalScale={1}
                fixedDecimalScale
              />
            </Text>
            <Icon alias={ICON_ALIASES.rateUp} color={theme.colors.green.primary} />
          </StyledRateUpContainer>
        )}
        {expensesData?.totalExpensesChangePercentage < 0 && (
          <StyledRateDownContainer>
            <Text size={theme.fontSizes.smallX} weight={700} color={theme.colors.red.primary} inline ignoreSpan>
              <NumericFormat
                displayType="text"
                value={expensesData?.totalExpensesChangePercentage}
                suffix="%"
                decimalScale={1}
                fixedDecimalScale
                allowNegative
              />
            </Text>
            <Icon alias={ICON_ALIASES.rateDown} color={theme.colors.red.primary} />
          </StyledRateDownContainer>
        )}
      </StyledSubtitleContainer>
      <Filters />
      <StyledChartContainer>
        <Chart type={CHART_TYPES.line} {...chartProps} />
      </StyledChartContainer>
    </Card>
  );
};

export default BusinessSnapshotView;
